import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useSavedGroupsActions, useSavedGroupsState } from "./SavedGroupsContext/SavedGroupsContext";
import { isOptimisticId } from "./SavedGroupsContext/useSavedGroups";
import GroupDeleteConfirm from "./GroupDeleteConfirm";
import SavedGroupsLoadError from "./SavedGroupsLoadError";

const bookmarkBtnStyle = (saved) => ({
  background: "none",
  border: "none",
  cursor: "pointer",
  color: saved ? "#533dd1" : "#394045",
  padding: 0,
  flexShrink: 0,
});

const dropdownStyle = {
  position: "absolute",
  top: "calc(100% + 4px)",
  right: 0,
  zIndex: 200,
  background: "#fff",
  border: "1px solid #e0e0e0",
  borderRadius: 10,
  boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
  minWidth: 200,
  padding: "6px 0",
};

const rowStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "8px 14px",
  cursor: "pointer",
  fontSize: 14,
  color: "#212528",
  userSelect: "none",
};

const dividerStyle = {
  height: 1,
  background: "#f0f0f0",
  margin: "4px 0",
};

const newGroupInputStyle = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "6px 14px",
};

function SavedGroupsBookmark({ sourceableType, sourceableId, sourceMeta = {} }) {
  const state = useSavedGroupsState();
  const actions = useSavedGroupsActions();
  const [open, setOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [showNewInput, setShowNewInput] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const ref = useRef(null);
  const inputRef = useRef(null);
  const creatingRef = useRef(false);
  const [isCreating, setIsCreating] = useState(false);

  const memberships = actions ? actions.membershipsFor(sourceableType, sourceableId) : [];
  const isSaved = memberships.length > 0;

  const closeDropdown = useCallback(() => {
    setOpen(false);
    setShowNewInput(false);
    setNewGroupName("");
    setConfirmDeleteId(null);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const handleOutsideClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) closeDropdown();
    };
    const handleEscape = (e) => {
      if (e.key === "Escape") closeDropdown();
    };
    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open, closeDropdown]);

  useEffect(() => {
    if (showNewInput && inputRef.current) inputRef.current.focus();
  }, [showNewInput]);

  // No SavedGroupsProvider above this bookmark, so there is nothing for it to read or write --
  // a host that mounts a single search panel on its own and wants no saved-groups UI does this
  // deliberately. Render nothing rather than a control that cannot answer. (The guard sits here,
  // below every hook, so the hook order stays the same on every render.)
  if (!state || !actions) return null;

  if (state.isLoading) {
    return (
      <button type="button" disabled style={{ ...bookmarkBtnStyle(false), cursor: "default", opacity: 0.4 }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z" />
        </svg>
      </button>
    );
  }

  const groups = state.groups || [];
  const groupIdsContaining = new Set(memberships.map((m) => m.saved_group_id));

  const membershipIn = (groupId) => memberships.find((m) => m.saved_group_id === groupId);

  // A row is off limits while either half of it is still local: a group whose create has not
  // answered has no id to file into, and a membership whose add has not answered has no id to
  // remove by. Both settle within one request, and the row becomes clickable again then.
  const isRowPending = (group) => isOptimisticId(group.id) || isOptimisticId(membershipIn(group.id)?.id);

  const handleToggleGroup = async (group) => {
    if (isRowPending(group)) return;

    if (groupIdsContaining.has(group.id)) {
      const membership = membershipIn(group.id);
      if (membership) await actions.removeFromGroup(group.id, membership.id, sourceableType, sourceableId);
    } else {
      await actions.addToGroup(group.id, sourceableType, sourceableId, sourceMeta);
    }
  };

  const handleCreateGroup = async () => {
    const trimmed = newGroupName.trim();
    if (!trimmed || creatingRef.current) return;
    creatingRef.current = true;
    setIsCreating(true);
    try {
      const group = await actions.createGroup(trimmed);
      if (!group) return;
      setNewGroupName("");
      setShowNewInput(false);
      if (!groupIdsContaining.has(group.id)) {
        await actions.addToGroup(group.id, sourceableType, sourceableId, sourceMeta);
      }
    } finally {
      creatingRef.current = false;
      setIsCreating(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleCreateGroup();
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        style={bookmarkBtnStyle(isSaved)}
        onClick={() => setOpen((o) => !o)}
        title={isSaved ? "Saved — click to manage" : "Save to group"}
        aria-pressed={isSaved}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill={isSaved ? "currentColor" : "none"}
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z" />
        </svg>
      </button>

      {open && (
        <div style={dropdownStyle} role="menu">
          {state.error && (
            <SavedGroupsLoadError error={state.error} onRetry={actions.reload} style={{ padding: "6px 14px" }} />
          )}
          {groups.length === 0 && !showNewInput && !state.error && (
            <div style={{ padding: "8px 14px", fontSize: 13, color: "#9ca3af" }}>No groups yet</div>
          )}
          {groups.map((group) => {
            const isOptimistic = isRowPending(group);

            if (confirmDeleteId === group.id) {
              return (
                <div key={group.id} style={{ ...rowStyle, gap: 6 }}>
                  <GroupDeleteConfirm
                    group={group}
                    onConfirm={() => {
                      actions.destroyGroup(group.id);
                      setConfirmDeleteId(null);
                    }}
                    onCancel={() => setConfirmDeleteId(null)}
                  />
                </div>
              );
            }

            return (
              <div
                key={group.id}
                role="menuitemcheckbox"
                aria-checked={groupIdsContaining.has(group.id)}
                aria-disabled={isOptimistic}
                style={{
                  ...rowStyle,
                  opacity: isOptimistic ? 0.5 : 1,
                  cursor: isOptimistic ? "default" : "pointer",
                }}
                onClick={isOptimistic ? undefined : () => handleToggleGroup(group)}
                onKeyDown={isOptimistic ? undefined : (e) => e.key === "Enter" && handleToggleGroup(group)}
                tabIndex={isOptimistic ? -1 : 0}
              >
                <span
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: 4,
                    flexShrink: 0,
                    border: groupIdsContaining.has(group.id) ? "none" : "1.5px solid #d2d3d4",
                    background: groupIdsContaining.has(group.id) ? "#533dd1" : "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {groupIdsContaining.has(group.id) && (
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M2 6l3 3 5-5"
                        stroke="#fff"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </span>
                <span style={{ flex: 1 }}>{group.name}</span>
                <span style={{ fontSize: 12, color: "#9ca3af" }}>{group.member_count}</span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmDeleteId(group.id);
                  }}
                  title="Delete group"
                  className="text-destructive-80"
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "0 0 0 6px",
                    flexShrink: 0,
                    lineHeight: 0,
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
                    <path d="M10 11v6M14 11v6" />
                    <path d="M9 6V4h6v2" />
                  </svg>
                </button>
              </div>
            );
          })}

          <div style={dividerStyle} />

          {showNewInput ? (
            <div style={newGroupInputStyle}>
              <input
                ref={inputRef}
                type="text"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Group name…"
                style={{
                  flex: 1,
                  border: "1px solid #d2d3d4",
                  borderRadius: 6,
                  padding: "4px 8px",
                  fontSize: 13,
                  outline: "none",
                }}
              />
              <button
                type="button"
                onClick={handleCreateGroup}
                disabled={!newGroupName.trim() || isCreating}
                style={{
                  background: "#533dd1",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  padding: "4px 10px",
                  fontSize: 12,
                  cursor: newGroupName.trim() ? "pointer" : "not-allowed",
                  opacity: newGroupName.trim() ? 1 : 0.5,
                }}
              >
                Add
              </button>
            </div>
          ) : (
            <div
              role="menuitem"
              style={{ ...rowStyle, color: "#533dd1", fontWeight: 500 }}
              onClick={() => setShowNewInput(true)}
              onKeyDown={(e) => e.key === "Enter" && setShowNewInput(true)}
              tabIndex={0}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              New group
            </div>
          )}
        </div>
      )}
    </div>
  );
}

SavedGroupsBookmark.propTypes = {
  sourceableType: PropTypes.string.isRequired,
  sourceableId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  // Display metadata captured from the search hit so freshly-saved sources render their
  // name/thumbnail immediately, without waiting for a page reload to repopulate the index
  // from saved_sources_index.
  sourceMeta: PropTypes.shape({
    name: PropTypes.string,
    imageUrl: PropTypes.string,
    slug: PropTypes.string,
  }),
};

export default SavedGroupsBookmark;
